import { tuning } from '@/config/tuning';
import { Rng } from '@/core/Rng';
import { clamp, lerp, shortestAngleDelta } from '@/math/angles';
import { forwardX, forwardZ, rightX, rightZ } from '@/math/orientation';
import type { GoalFrame } from '@/physics/goalNet';

/**
 * Track.ts — the procedural course, as PURE data + math (no three.js, no DOM).
 *
 * The track is a finite, point-to-point sprint generated up front from a seed.
 * The centreline is an array of nodes; everything else (road mesh, walls,
 * physics, AI, standings) reads from this via sampling/projection helpers.
 *
 * Key §6 guarantees baked in here:
 *  - ONE orientation convention (forward = sin/cos of `dir`; see math/orientation).
 *  - The heading is clamped to ±headingClampRad of "forward" with a centering
 *    bias, so the course never spirals back and overlaps itself. (Because the
 *    clamp keeps |dir| < 90°, cos(dir) > 0, so z strictly increases every step.)
 *  - heightAt() interpolates along the nearest road SEGMENT (closest point on
 *    the segment), giving a CONTINUOUS ground height — no stair-step on hills.
 *  - nearestNodeIndex() does a windowed local search around a hint for speed.
 */

export interface TrackNode {
  x: number;
  y: number;
  z: number;
  /** Tangent heading at this node: the direction from here to the next node. */
  dir: number;
  /** Unit rightward normal on the ground plane (perpendicular to the tangent). */
  rightX: number;
  rightZ: number;
}

/** A flat wall plane across the road at a course end (M42). */
export interface PlaneWall {
  x: number;
  z: number;
  /** Unit normal pointing INTO the course. */
  nx: number;
  nz: number;
}

/** A point sampled at a given arc-length distance along the centreline. */
export interface TrackSample {
  x: number;
  y: number;
  z: number;
  dir: number;
  rightX: number;
  rightZ: number;
  distance: number;
}

/** The result of projecting an arbitrary (x,z) point onto the road. */
export interface RoadProjection {
  /** Index of the segment's start node. */
  index: number;
  /** 0..1 position along that segment. */
  t: number;
  /** Closest point on the centreline. */
  centerX: number;
  centerZ: number;
  /** Continuous ground height at the projection. */
  y: number;
  /** Interpolated tangent heading. */
  dir: number;
  /** Interpolated right normal. */
  rightX: number;
  rightZ: number;
  /** Signed distance from the centreline along `right` (+ = right side). */
  lateral: number;
  /** Arc-length from the start to the projection. */
  distance: number;
}

export interface TrackConfig {
  step: number;
  halfWidth: number;
  nodeCount: number;
  turnCurvatureMin: number;
  turnCurvatureMax: number;
  headingClampRad: number;
  sectionLenMin: number;
  sectionLenMax: number;
}

export class Track {
  readonly nodes: readonly TrackNode[];
  readonly step: number;
  readonly halfWidth: number;
  /** Total arc length of the centreline. */
  readonly length: number;
  /** Arc-length position of the finish line (a little before the very end). */
  readonly finishDistance: number;
  /** M117c: shortcut CHANNELS opt out of the course end-walls — their entry
   *  must stay open both ways (backing off the deck edge drops you home) and
   *  a long-standoff catch briefly flies before node 0. Courses keep them. */
  noEndWalls = false;
  /** M117d: shortcut CHANNELS are downhill CHUTES — a ball on one is pulled
   *  along the falling deck by this effective gravity (0 = an ordinary road,
   *  no pull). Set only by placeShortcuts, so the course's ball physics (and
   *  the locked AI pace ladder) can never feel it. See tuning.shortcuts. */
  chuteRoll = 0;

  constructor(nodes: TrackNode[], step: number, halfWidth: number, finishDistance: number) {
    this.nodes = nodes;
    this.step = step;
    this.halfWidth = halfWidth;
    this.length = (nodes.length - 1) * step;
    this.finishDistance = finishDistance;
  }

  get start(): TrackNode {
    return this.nodes[0];
  }

  get finish(): TrackNode {
    return this.nodes[this.nodes.length - 1];
  }

  /** Course end-wall planes (M42): `back` sits `tuning.track.startBarrier`
   *  behind the start grid, `end` sits `tuning.track.endBarrier` past the last
   *  node. Normals point INTO the course (for resolvePlaneWall). Computed once. */
  get endWalls(): { back: PlaneWall; end: PlaneWall } {
    if (!this._endWalls) {
      const s = this.nodes[0];
      const e = this.nodes[this.nodes.length - 1];
      const sfx = forwardX(s.dir);
      const sfz = forwardZ(s.dir);
      const efx = forwardX(e.dir);
      const efz = forwardZ(e.dir);
      this._endWalls = {
        back: {
          x: s.x - sfx * tuning.track.startBarrier,
          z: s.z - sfz * tuning.track.startBarrier,
          nx: sfx,
          nz: sfz,
        },
        end: {
          x: e.x + efx * tuning.track.endBarrier,
          z: e.z + efz * tuning.track.endBarrier,
          nx: -efx,
          nz: -efz,
        },
      };
    }
    return this._endWalls;
  }
  private _endWalls: { back: PlaneWall; end: PlaneWall } | null = null;

  /** THE FINISH GOAL (M157): the goal-line centre + its axes, from the finish
   *  sample (the line the sim's finish rule uses — a ball's centre crossing
   *  it). The net behind it catches balls and cars (physics/goalNet.ts).
   *  Null on a shortcut channel (noEndWalls) — no goal lives inside a deck. */
  get goalFrame(): GoalFrame | null {
    if (this.noEndWalls) return null;
    if (!this._goalFrame) {
      const s = this.sampleAt(this.finishDistance);
      this._goalFrame = {
        x: s.x,
        y: s.y,
        z: s.z,
        fx: forwardX(s.dir),
        fz: forwardZ(s.dir),
        rx: s.rightX,
        rz: s.rightZ,
        halfWidth: this.halfWidth,
      };
    }
    return this._goalFrame;
  }
  private _goalFrame: GoalFrame | null = null;

  /**
   * Index of the centreline node nearest to (x, z). If `hint` is given (e.g. the
   * last known index), only a small window around it is searched — O(window)
   * instead of O(n) — which is what we use every frame during a race.
   */
  nearestNodeIndex(x: number, z: number, hint?: number): number {
    const n = this.nodes.length;
    let lo = 0;
    let hi = n - 1;
    if (hint != null) {
      lo = Math.max(0, hint - 25);
      hi = Math.min(n - 1, hint + 60);
    }
    let best = lo;
    let bestD = Infinity;
    for (let i = lo; i <= hi; i++) {
      const nd = this.nodes[i];
      const dx = x - nd.x;
      const dz = z - nd.z;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /**
   * Project (x, z) onto the road: returns the closest point on the centreline,
   * the continuous ground height there, the tangent/right vectors, the signed
   * lateral offset, and the arc-length distance. The heart of "continuous
   * height" and the lateral wall-clamp.
   */
  project(x: number, z: number, hint?: number): RoadProjection {
    const i0 = this.nearestNodeIndex(x, z, hint);
    const n = this.nodes.length;
    let best: RoadProjection | null = null;
    let bestD = Infinity;

    // Test the two segments adjacent to the nearest node and keep the closer.
    for (const seg of [i0 - 1, i0]) {
      if (seg < 0 || seg >= n - 1) continue;
      const a = this.nodes[seg];
      const b = this.nodes[seg + 1];
      const abx = b.x - a.x;
      const abz = b.z - a.z;
      const len2 = abx * abx + abz * abz || 1;
      const t = clamp(((x - a.x) * abx + (z - a.z) * abz) / len2, 0, 1);
      const cx = a.x + abx * t;
      const cz = a.z + abz * t;
      const dx = x - cx;
      const dz = z - cz;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        const dir = a.dir + shortestAngleDelta(a.dir, b.dir) * t;
        const rx = rightX(dir);
        const rz = rightZ(dir);
        best = {
          index: seg,
          t,
          centerX: cx,
          centerZ: cz,
          y: lerp(a.y, b.y, t),
          dir,
          rightX: rx,
          rightZ: rz,
          lateral: dx * rx + dz * rz,
          distance: (seg + t) * this.step,
        };
      }
    }

    if (!best) {
      // Only possible with a degenerate (<2 node) track.
      throw new Error('Track.project: no valid segment near the point');
    }
    return best;
  }

  /** Continuous ground height at (x, z). */
  heightAt(x: number, z: number, hint?: number): number {
    return this.project(x, z, hint).y;
  }

  /** Race progress 0..1 measured to the FINISH line (for HUD + standings). */
  progressToFinish(x: number, z: number, hint?: number): number {
    return clamp(this.project(x, z, hint).distance / this.finishDistance, 0, 1);
  }

  /** Sample the centreline at an absolute arc-length distance. */
  sampleAt(distance: number): TrackSample {
    const d = clamp(distance, 0, this.length);
    const fi = d / this.step;
    let seg = Math.floor(fi);
    if (seg >= this.nodes.length - 1) seg = this.nodes.length - 2;
    const t = fi - seg;
    const a = this.nodes[seg];
    const b = this.nodes[seg + 1];
    const dir = a.dir + shortestAngleDelta(a.dir, b.dir) * t;
    return {
      x: lerp(a.x, b.x, t),
      y: lerp(a.y, b.y, t),
      z: lerp(a.z, b.z, t),
      dir,
      rightX: rightX(dir),
      rightZ: rightZ(dir),
      distance: d,
    };
  }
}

/**
 * Generate a track deterministically from a seed. Quick Race passes a random
 * seed; the Tour passes a fixed per-city seed so a city always plays the same.
 */
export function generateTrack(seed: number, cfg: TrackConfig = tuning.track): Track {
  const rng = new Rng(seed);
  const nodes: TrackNode[] = [];

  // Rolling hills: a few smooth sine octaves summed together. Low frequencies
  // mean long, gentle rises/dips rather than jagged bumps.
  const octaves = [0, 0, 0].map(() => ({
    amp: rng.range(1.5, 4.5),
    freq: rng.range(0.012, 0.06),
    phase: rng.range(0, Math.PI * 2),
  }));
  const rawElevation = (i: number): number =>
    octaves.reduce((sum, o) => sum + o.amp * Math.sin(i * o.freq + o.phase), 0);
  const elevation0 = rawElevation(0);
  const elevation = (i: number): number => rawElevation(i) - elevation0; // start at y=0

  const centeringGain = 0.03; // gentle pull back toward "forward" so it can't spiral
  let x = 0;
  let z = 0;
  let dir = 0;
  let curvature = 0;
  let sectionRemaining = 24; // a straight start section so racers can get going

  for (let i = 0; i < cfg.nodeCount; i++) {
    nodes.push({ x, y: elevation(i), z, dir, rightX: rightX(dir), rightZ: rightZ(dir) });

    // Advance along the current tangent (exactly `step` units, so arc length is
    // simply (nodeCount - 1) * step and progress is easy to reason about).
    x += forwardX(dir) * cfg.step;
    z += forwardZ(dir) * cfg.step;

    // Start a new straight-or-curve section when the current one runs out.
    if (sectionRemaining <= 0) {
      sectionRemaining = rng.int(cfg.sectionLenMin, cfg.sectionLenMax);
      if (rng.next() < 0.35) {
        curvature = 0; // a straight
      } else {
        const sign = rng.next() < 0.5 ? -1 : 1;
        curvature = sign * rng.range(cfg.turnCurvatureMin, cfg.turnCurvatureMax);
      }
    }
    sectionRemaining--;

    dir += curvature; // apply this section's turn
    dir -= dir * centeringGain; // centering bias toward forward
    dir = clamp(dir, -cfg.headingClampRad, cfg.headingClampRad); // hard clamp (no spiral)
  }

  // Finish a little before the very end, leaving some run-out room.
  const finishDistance = Math.max(0, cfg.nodeCount - 12) * cfg.step;
  return new Track(nodes, cfg.step, cfg.halfWidth, finishDistance);
}
